// Push Notification Service for Web Push API
// Handles subscription, permission requests, and notification management

import api from './api';

class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this.subscription = null;
    this.vapidPublicKey = null;
  }

  /**
   * Check if push notifications are supported
   */
  isPushSupported() {
    return this.isSupported;
  }

  /**
   * Check if user has granted notification permission
   */
  async hasPermission() {
    if (!this.isSupported) return false;
    return Notification.permission === 'granted';
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Register service worker
   */
  async registerServiceWorker() {
    if (!this.isSupported) {
      throw new Error('Service Worker is not supported in this browser');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered successfully:', this.registration);
      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey() {
    try {
      const response = await api.get('/admin/notifications/web-push/vapid-key/');
      this.vapidPublicKey = response.data.public_key;
      console.log('VAPID Public Key received:', this.vapidPublicKey);
      console.log('VAPID Key length:', this.vapidPublicKey.length);
      return response.data;
    } catch (error) {
      console.error('Error getting VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported in this browser');
    }

    if (!this.registration) {
      await this.registerServiceWorker();
    }

    try {
      // Get VAPID public key
      if (!this.vapidPublicKey) {
        await this.getVapidPublicKey();
      }

      // Check if already subscribed and unsubscribe first
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // Subscribe to push manager with new VAPID key
      const applicationServerKey = this.urlBase64ToUint8Array(this.vapidPublicKey);
      
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });


      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  /**
   * Send subscription info to backend
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      const response = await api.post('/admin/notifications/web-push/subscribe/', {
        subscription: subscription
      });
      return response.data;
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.subscription) {
      console.log('No active subscription to unsubscribe from');
      return true;
    }

    try {
      const result = await this.subscription.unsubscribe();
      this.subscription = null;
      console.log('Push unsubscription successful:', result);
      return result;
    } catch (error) {
      console.error('Push unsubscription failed:', error);
      throw error;
    }
  }


  /**
   * Get current subscription status
   */
  async getSubscriptionStatus() {
    if (!this.isSupported) {
      return {
        supported: false,
        subscribed: false,
        permission: 'denied'
      };
    }

    try {
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      const subscription = await this.registration.pushManager.getSubscription();
      this.subscription = subscription;

      return {
        supported: true,
        subscribed: !!subscription,
        permission: Notification.permission,
        subscription: subscription
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return {
        supported: true,
        subscribed: false,
        permission: Notification.permission,
        error: error.message
      };
    }
  }

  /**
   * Initialize push notifications (complete setup)
   */
  async initialize() {
    try {
      console.log('Initializing push notifications...');

      // Check support
      if (!this.isSupported) {
        throw new Error('Push notifications are not supported in this browser');
      }

      // Register service worker
      await this.registerServiceWorker();

      // Check permission
      if (Notification.permission === 'default') {
        const granted = await this.requestPermission();
        if (!granted) {
          throw new Error('Notification permission denied by user');
        }
      } else if (Notification.permission === 'denied') {
        throw new Error('Notification permission denied by user');
      }

      // Subscribe to push notifications
      await this.subscribe();

      console.log('Push notifications initialized successfully');
      return true;
    } catch (error) {
      console.error('Push notification initialization failed:', error);
      throw error;
    }
  }

  /**
   * Show local notification (for testing)
   */
  showLocalNotification(title, options = {}) {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/static/images/notification-icon.png',
        badge: '/static/images/badge-icon.png',
        tag: 'checkstall-local',
        ...options
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } else {
      console.warn('Notification permission not granted');
      return null;
    }
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;

